/**
 * components/statTile.tsx
 * KPI stat tile from the design — a card with an optional tone dot + label,
 * a large value, and an optional sub-line. Used by the Dashboard stat grid
 * (clickable) and the Compliance stat cards (static). Hover-lifts on web when
 * an `onPress` is provided.
 */
import React from 'react';
import { View, Text, Pressable } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useHover } from '../hooks/useHover';
import { web } from '../utils/web';
import { toneColors, type Tone } from '../theme/tone';

export interface StatTileProps {
  label: string;
  value: string | number;
  sub?: string;
  /** Tone for the leading dot + value accent. Omit for plain ink value. */
  tone?: Tone;
  onPress?: () => void;
}

export default function StatTile({ label, value, sub, tone, onPress }: StatTileProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii, spacing } = theme;
  const { hovered, hoverProps } = useHover();
  const clickable = !!onPress;

  return (
    <Pressable
      {...hoverProps}
      onPress={onPress}
      disabled={!clickable}
      style={[
        {
          flex: 1,
          minWidth: 180,
          padding: spacing[4],
          borderRadius: radii.lg,
          backgroundColor: colors.card,
          borderWidth: 1,
          borderColor: colors.line,
        },
        web({
          boxShadow: '0 1px 0 rgba(16,24,40,0.04)',
          transition: 'transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease',
          cursor: clickable ? 'pointer' : 'default',
        }),
        clickable && hovered && web({
          transform: 'translateY(-1px)',
          boxShadow: '0 6px 18px -8px rgba(16,24,40,0.18)',
          borderColor: colors.lineStrong,
        }),
      ]}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: spacing[2] }}>
        {tone && <View style={{ width: 7, height: 7, borderRadius: radii.pill, backgroundColor: toneColors(colors, tone).fg }} />}
        <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontWeight: fontWeight.medium }}>{label}</Text>
      </View>
      <Text style={{ fontSize: fontSize['3xl'], fontWeight: fontWeight.semibold, color: colors.ink, letterSpacing: -0.5 }}>
        {value}
      </Text>
      {!!sub && <Text style={{ fontSize: fontSize.xs, color: colors.ink4, marginTop: 2 }}>{sub}</Text>}
    </Pressable>
  );
}
